import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSearchParams } from 'react-router-dom';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { useGetRoles } from '@/hooks/use-company';
import { useAddArchitectTeamMembers, useAddSubcontractorTeamMembers, useGetArchitect, useGetSubcontractor } from '@/hooks/use-contractors';
import { toast } from '@/hooks/use-toast';
import OnboardingWrapper from '@/components/ui/OnboardingWrapper';
import { EXTENDED_STEPS } from '@/utils/onboarding-steps';
import { TeamMemberFormCard, TeamMemberFormContainer } from '@/components/ui/team-member-form';
import { AdministratorCard } from '@/components/ui/administrator-card';
import { RolePermissionsCard } from '@/components/ui/role-permissions-card';
import { ContactInfoCard } from '@/components/ui/contact-info-card';
import { Form } from '@/components/ui/form';

const teamMemberSchema = z.object({
    id: z.string().optional(),
    fullName: z.string().optional(),
    email: z.string().optional(),
    roleIds: z.array(z.string()).optional().default([]),
}).refine((data) => {
    // If any field is filled, then all required fields must be filled
    const hasAnyData = data.fullName || data.email || (data.roleIds && data.roleIds.length > 0);

    if (!hasAnyData) return true; // If no data, it's valid (optional)

    // If has any data, validate all required fields
    const isValid =
        data.fullName && data.fullName.trim().length > 0 &&
        data.email && data.email.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email) &&
        data.roleIds && data.roleIds.length > 0;

    return isValid;
}, {
    message: "If adding a team member, full name, valid email, and at least one role are required",
    path: ["fullName"],
});

const addUsersSchema = z.object({
    teamMembers: z.array(teamMemberSchema).optional().default([]),
});

type AddUsersFormData = z.infer<typeof addUsersSchema>;

export default function AddUsersStep() {
    const [searchParams] = useSearchParams();
    const { handleError } = useErrorHandler();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const organizationId = searchParams.get('orgId');
    const accountType = searchParams.get('type') as 'architect' | 'subcontractor';

    // API hooks
    const { data: rolesData, isLoading: isLoadingRoles } = useGetRoles();
    const addArchitectTeamMembers = useAddArchitectTeamMembers();
    const addSubcontractorTeamMembers = useAddSubcontractorTeamMembers();

    // Fetch organization data based on account type
    const { data: architectData, isLoading: isLoadingArchitect } = useGetArchitect(
        accountType === 'architect' ? organizationId : null
    );
    const { data: subcontractorData, isLoading: isLoadingSubcontractor } = useGetSubcontractor(
        accountType === 'subcontractor' ? organizationId : null
    );

    // Optimized role options memo
    const roleOptions = useMemo(
        () =>
            rolesData?.data?.map((role) => ({
                value: role.id,
                label: role.name,
            })) || [],
        [rolesData]
    );

    // Get admin data based on account type (using profile data since that's who created the organization)
    const adminData = (() => {
        if (accountType === 'architect' && architectData) {
            const profile = architectData.architectSpecificProfile;
            return {
                fullName: profile?.primaryContactFirstName && profile?.primaryContactLastName
                    ? `${profile.primaryContactFirstName} ${profile.primaryContactLastName}`
                    : architectData.name || 'Admin Name',
                email: profile?.primaryContactEmail || architectData.businessEmail || 'admin@company.com',
                role: profile?.primaryContactRole || 'Admin'
            };
        } else if (accountType === 'subcontractor' && subcontractorData) {
            const profile = subcontractorData.subcontractorSpecificProfile;
            return {
                fullName: profile?.primaryContactFirstName && profile?.primaryContactLastName
                    ? `${profile.primaryContactFirstName} ${profile.primaryContactLastName}`
                    : subcontractorData.name || 'Admin Name',
                email: profile?.primaryContactEmail || subcontractorData.businessEmail || 'admin@company.com',
                role: profile?.primaryContactRole || 'Admin'
            };
        }
        return {
            fullName: 'Admin Name',
            email: 'admin@company.com',
            role: 'Admin'
        };
    })();

    const form = useForm<AddUsersFormData>({
        resolver: zodResolver(addUsersSchema),
        defaultValues: {
            teamMembers: [{ id: undefined, fullName: '', email: '', roleIds: [] }],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: 'teamMembers',
    });

    // Effect to prefill existing team members (including all users from the organization)
    useEffect(() => {
        const organizationData = accountType === 'architect' ? architectData : subcontractorData;
        if (organizationData?.users && organizationData.users.length > 0) {
            // Include ALL users in team members section
            const allUsers = organizationData.users;

            // Clear existing fields and add all users
            remove(); // Remove all existing fields
            allUsers.forEach(user => {
                append({
                    id: user.id,
                    fullName: user.fullName,
                    email: user.email,
                    roleIds: user.roles?.map(role => role.roleId) || [] // Use roles from API response
                });
            });

            // Add one empty field for new additions
            append({ id: undefined, fullName: '', email: '', roleIds: [] });
        }
    }, [accountType, architectData, subcontractorData, append, remove]);

    // Optimized add team member function
    const addTeamMember = useCallback(() => {
        append({ id: undefined, fullName: '', email: '', roleIds: [] });
    }, [append]);

    // Optimized remove team member function
    const removeTeamMember = useCallback(
        (index: number) => {
            remove(index);
        },
        [remove]
    );

    // Calculate ready users count
    const getReadyUsersCount = () => {
        const values = form.watch('teamMembers') || [];
        return values.filter(member =>
            member?.fullName && member?.email && member?.roleIds?.length > 0
        ).length;
    };

    const handleNext = async (data: AddUsersFormData) => {
        if (!organizationId) {
            toast({
                title: 'Error',
                description: 'Organization ID is missing. Please try again.',
                variant: 'destructive',
            });
            return false;
        }

        if (!accountType || (accountType !== 'architect' && accountType !== 'subcontractor')) {
            toast({
                title: 'Error',
                description: 'Invalid account type. Please try again.',
                variant: 'destructive',
            });
            return false;
        }

        // If no team members are provided, just proceed to next step
        if (!data.teamMembers || data.teamMembers.length === 0) {
            toast({
                title: 'Step Completed',
                description: 'Proceeding to next step. You can add team members later.',
                variant: 'success',
            });
            return true;
        }

        // Filter out empty team members (those with no name and email)
        const validTeamMembers = data.teamMembers.filter(
            member => member.fullName?.trim() && member.email?.trim()
        );

        if (validTeamMembers.length === 0) {
            toast({
                title: 'Step Completed',
                description: 'Proceeding to next step. You can add team members later.',
                variant: 'success',
            });
            return true;
        }

        setIsSubmitting(true);
        try {
            // Prepare the payload
            const payload = {
                teamMembers: validTeamMembers.map((member) => {
                    const teamMemberData: {
                        id?: string;
                        fullName: string;
                        email: string;
                        roleIds: string[];
                        alreadyExists: boolean;
                    } = {
                        fullName: member.fullName!,
                        email: member.email!,
                        roleIds: member.roleIds || [],
                        alreadyExists: !!member.id, // true if id exists, false if new member
                    };

                    // Only include id if the member already exists
                    if (member.id) {
                        teamMemberData.id = member.id;
                    }

                    return teamMemberData;
                }),
            };

            console.log('Team members payload:', payload);

            // Call the appropriate API based on account type
            if (accountType === 'architect') {
                await addArchitectTeamMembers.mutateAsync({
                    architectId: organizationId,
                    data: payload,
                });
            } else if (accountType === 'subcontractor') {
                await addSubcontractorTeamMembers.mutateAsync({
                    subcontractorId: organizationId,
                    data: payload,
                });
            }

            toast({
                title: 'Team Members Added',
                description: `${validTeamMembers.length} team member(s) added successfully.`,
                variant: 'success',
            });

            return true;
        } catch (error: any) {
            handleError(error, {
                customMessage: 'Failed to add team members. Please try again.',
            });
            return false;
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <OnboardingWrapper
            currentStep={6}
            totalSteps={7}
            steps={EXTENDED_STEPS}
            accountType={accountType}
            onNext={async () => {
                const isValid = await form.trigger();
                if (!isValid) return false;

                return new Promise((resolve) => {
                    form.handleSubmit(async (data) => {
                        const result = await handleNext(data);
                        resolve(result !== false);
                    })();
                });
            }}
            isLoading={isSubmitting}
            saveButtonText="Save & Continue"
        >
            <div className="space-y-6">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight text-foreground mb-2">
                        Add Users
                    </h2>
                    <p className="text-muted-foreground">
                        Add team members who will have access to the LarkPM platform. You can add more users later or skip this step for now.
                    </p>
                </div>

                <Form {...form}>
                    <div className="space-y-4">
                        {/* Show loading state while fetching roles or organization data */}
                        {(isLoadingRoles || isLoadingArchitect || isLoadingSubcontractor) && (
                            <div className="flex items-center justify-center py-4">
                                <div className="w-6 h-6 border-2 border-outline-variant border-t-slate-600 rounded-full animate-spin" />
                                <span className="ml-2 text-muted-foreground">Loading data...</span>
                            </div>
                        )}

                        {/* Administrator Section */}
                        <AdministratorCard
                            fullName={adminData.fullName}
                            email={adminData.email}
                            role={adminData.role}
                        />

                        {/* Add Team Members Section */}
                        <ContactInfoCard title="Add Team Members">
                            <TeamMemberFormContainer
                                mode="simple"
                                onAddMember={addTeamMember}
                                addButtonText="Add Another Team Member"
                                addButtonStyle="center"
                            >
                                {fields.map((field, index) => (
                                    <TeamMemberFormCard
                                        key={field.id}
                                        mode="simple"
                                        index={index}
                                        fieldId={field.id}
                                        canRemove={fields.length > 1}
                                        onRemove={() => removeTeamMember(index)}
                                        simpleProps={{
                                            control: form.control,
                                            roleOptions,
                                            isLoadingRoles
                                        }}
                                    />
                                ))}
                            </TeamMemberFormContainer>
                        </ContactInfoCard>

                        {/* User Role Permissions Section */}
                        <RolePermissionsCard />

                        {/* Blue info tab at the bottom - Always visible */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="text-blue-800 font-medium mb-1">
                                Users ready to invite: {getReadyUsersCount()}/{fields.length}
                            </h4>
                            <p className="text-blue-700 text-sm">
                                Invitations will be sent after your account is approved
                            </p>
                        </div>
                    </div>
                </Form>
            </div>
        </OnboardingWrapper>
    );
}
